<template>
  <div class="flex flex-col flex-auto text-center">
    <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
      {{ errorMessage }}
    </div>
    <div v-else-if="isLoading">
      <LayupSpinner />
      <p class="text-black">Loading...</p>
    </div>
    <div v-else class="flex flex-col flex-auto justify-between">
      <div class="w-full">
        <div class="h-min-max">
          <div class="my-2">
            <WidgetMerchantLogo />
            <p class="text-base-black my-3 text-sm font-medium">Total: {{ currency(widgetStore.order.amountDue) }}</p>
          </div>

          <p class="font-semibold text-3xl text-base-black my-3">{{ instalmentAmount }} pm</p>

          <div
            v-if="widgetStore.order.hideBillingDate"
            class="flex place-content-center w-auto"
            data-testid="billingDay"
          >
            <p class="text-base-black text-sm font-medium">
              Monthly payment date
              <span class="text-brand-700 mr-2 font-bold">{{ billingDay }}{{ nthNumber(billingDay) }}</span>
            </p>
          </div>
          <SelectInput
            v-else
            :label="`Select monthly payment date`"
            :selections="monthlyBillingDays"
            v-model="billingDay"
            data-testid="billingDay"
          />
          <SelectInput
            v-if="durationOptions.length > 1"
            :label="`Select duration`"
            :selections="durationOptions"
            v-model="duration"
            data-testid="duration"
          />
          <div v-else class="flex place-content-center w-auto" data-testid="duration">
            <p class="text-base-black text-sm font-medium">
              Duration
              <span class="text-brand-700 mr-2 font-bold"
                >{{ duration }} {{ pluralOrSingularText(duration, 'month') }}</span
              >
            </p>
          </div>

          <div class="flex place-content-center w-auto">
            <p class="text-base-black text-sm font-medium">
              Deposit amount
              <span class="text-brand-700 mr-2 font-bold">{{ depositDue }}</span>
            </p>
          </div>

          <div class="flex justify-center items-center my-4">
            <Button
              variant="outline"
              @click="router.push({ name: 'PaymentPlanBreakdown' })"
              data-testid="paymentPlanBreakdown"
            >
              Payment breakdown <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
      <div>
        <div class="flex text-left px-2 gap-2 items-center">
          <CheckboxComponent v-model="acceptTermsAndConditions" name="acceptTermsAndConditions" id="terms-checkbox" />
          <label class="break-words text-sm text-gray-500" for="terms-checkbox">
            You agree to
            <a
              class="text-brand-800 underline hover:text-brand-500 cursor-pointer"
              color="text-brand-700"
              href="
                  https://res.cloudinary.com/layup/image/upload/fl_attachment/v1726666846/LayUp_User_Terms_and_Conditions_pzbn86.pdf
              "
              download
              >LayUp's</a
            >
            <span v-if="widgetStore.order.merchantTermsLink"
              >and
              <a
                class="text-brand-800 underline hover:text-brand-500 cursor-pointer"
                color="text-brand-700"
                target="_blank"
                :href="widgetStore.order.merchantTermsLink"
                >{{ widgetStore.order.merchantName }}'s</a
              >
            </span>
            terms and conditions
          </label>
        </div>
        <div class="pt-2">
          <RouterLink :to="{ name: 'WidgetSignIn' }" @click="onActivate">
            <button
              variant="outline-secondary"
              class="bg-brand-600 rounded w-full h-10 text-base-white hover:bg-brand-700 disabled:opacity-60 disabled:bg-brand-600"
              :disabled="!duration || !billingDay || !acceptTermsAndConditions"
            >
              Accept
            </button>
          </RouterLink>
        </div>
        <div class="pt-2 ml-1">
          <button
            variant="outline-secondary"
            class="bg-red-50 rounded w-full h-10 text-red-600 hover:bg-red-300 hover:text-white"
            @click="onCancelButtonClick"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SelectInput from '@/components/common/SelectInput.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import type { IPaymentPlanPreview, ITrimmedPaymentPlan } from '@/models/paymentPlans';
import type { IPayment } from '@/models/payments';
import router from '@/router';
import * as PaymentPlanService from '@/services/paymentPlans';
import { useWidgetStore } from '@/stores/widget';
import { currency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { determineBillingDay, getMonthlyBillingDays } from '@/utils/paymentPlan.utils';
import { nthNumber, pluralOrSingularText } from '@/utils/text.utils';
import { ChevronRight } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import WidgetMerchantLogo from '../../common/WidgetMerchantLogo.vue';

const isLoading = ref(false);
const errorMessage = ref('');

const widgetStore = useWidgetStore();

const route = useRoute();
const orderId = route?.params?.id as string;
const availableDurations = ref([0]);
const duration = ref(0);
const billingDay = ref(0);
const acceptTermsAndConditions = ref(false);
// Metrics
let orderLengthAdjusted = false;

if (route.query.paymentMethod || route.query.amount) {
  useRouter().replace({ name: 'PaymentLink', params: { id: orderId }, query: route.query });
}

const determineDuration = () => {
  if (availableDurations.value) {
    if (availableDurations.value.find((d) => d === widgetStore.layby?.duration)) {
      return widgetStore.layby?.duration;
    }
    return availableDurations.value[availableDurations.value.length - 1];
  }
  return 0;
};

const init = async () => {
  isLoading.value = true;
  try {
    // When billing date selection is disabled, always default to today's day-of-month (capped at the max billing day)
    billingDay.value = determineBillingDay(
      !widgetStore.order.hideBillingDate ? widgetStore.layby.billingDay : undefined,
      isDebiCheckEnabled.value,
    );
    await getPaymentPlanPreview(orderId, billingDay.value - 1);
    duration.value = determineDuration();
  } catch (error: any) {
    createException(error);
    errorMessage.value = 'Could not fetch order details. Please refresh to try again.';
  }
  isLoading.value = false;
};

const getPaymentPlanPreview = async (id: string, billingDay: number) => {
  try {
    const response = await PaymentPlanService.getPaymentPlanPreview(id, 'MONTHLY', billingDay);
    widgetStore.paymentPlanPreview = response as IPaymentPlanPreview;
    availableDurations.value = response.paymentPlans.map((pp: any) => pp.quantity);

    return response;
  } catch (error: any) {
    createException(error);
    errorMessage.value = 'Could not fetch payment plan. Please try again.';
  }
};

const initialisePaymentPlan = (quantity: number, billingDay?: number) => {
  widgetStore.layby = {
    duration: quantity,
    billingDay: billingDay!,
  };
};

const durationOptions = computed(() => {
  const options = availableDurations.value.map((i) => {
    const duration = i;
    return { value: duration, text: `${duration} ${pluralOrSingularText(duration, 'month')}` };
  });
  return options;
});

const isDebiCheckEnabled = computed(() => widgetStore.order?.paymentMethods?.debiCheck === true);

const monthlyBillingDays = computed(() => getMonthlyBillingDays(isDebiCheckEnabled.value));

const instalmentAmount = computed(() => {
  const payments = widgetStore?.paymentPlanPreview?.paymentPlans?.filter((pp) => pp.quantity === duration.value)[0]
    ?.payments as unknown as IPayment[];
  if (payments) {
    return currency(payments[1].amount);
  }
  return currency(0);
});

const depositDue = computed(() => {
  const currentPlan = widgetStore?.paymentPlanPreview?.paymentPlans?.filter(
    (pp) => pp.quantity === duration.value,
  )[0] as unknown as ITrimmedPaymentPlan;
  if (currentPlan) {
    return currency(currentPlan.deposit);
  }
  if (widgetStore?.paymentPlanPreview?.depositDue) {
    return currency(widgetStore?.paymentPlanPreview?.depositDue);
  }
  return currency(0);
});

watch(duration, (newValue) => {
  initialisePaymentPlan(parseInt(newValue.toString()), parseInt(billingDay.value.toString()));
});

watch(billingDay, async (newValue) => {
  isLoading.value = true;
  widgetStore.paymentPlanPreview = (await getPaymentPlanPreview(
    orderId!?.toString(),
    parseInt(newValue.toString()) - 1,
  )) as IPaymentPlanPreview;
  const maximumDuration = availableDurations.value[availableDurations.value.length - 1];
  if (duration.value > maximumDuration) {
    orderLengthAdjusted = true;
    duration.value = maximumDuration;
  }
  initialisePaymentPlan(duration.value, newValue);
  isLoading.value = false;
});

const orderConfigurationMetric = (action: 'activate' | 'cancel') => {
  addMetric(action === 'activate' ? 'activate_order' : 'order_configuration', {
    order_adjusted: `${orderLengthAdjusted}`,
    order_length: `${duration.value}`,
    billing_day: `${billingDay.value}`,
    channel: 'widget',
    action,
    merchantId: `${widgetStore.order.merchantId ?? ''}`,
    orderId: `${orderId ?? ''}`,
  });
};

const onActivate = () => {
  orderConfigurationMetric('activate');
};

const onCancelButtonClick = () => {
  orderConfigurationMetric('cancel');
  window.parent?.postMessage({ type: 'layup-close' }, '*');
  router.back();
};

init();
</script>
